import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import type { OperationResult } from 'urql';
import type {
  Exact,
  UnsubscribeFlexikontoMutation,
  UnsubscribeFromNewsletterMutation,
} from '@packages/gql/generated/shopping/graphql';
import type {
  GTMEventGlycerinDisplayError,
  GTMEventGlycerinFormSubmitClick,
  GTMEventGlycerinUnsubscribe,
} from '@packages/tracking/src/types/events';
import { GlycerinErrorSeverity } from '@packages/tracking/src/types/enums';
import type { Dispatch, SetStateAction } from 'react';
import { useSnackbar } from '@packages/shared/src/components/Notification/SnackbarContext';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useFormDeviceType } from '../../GeneralForm/helpers/useFormDeviceType';
import { useExecuteUnsubscribe } from './useExecuteUnsubscribe';
import type { SetErrorType } from '../types';
import { generalFormMessages } from '../../messages';
import type { FormSubmitResult } from '../../types';
import { submitMessage } from '../../GeneralForm/helpers/handleSubmit';
import { convertFormDataToPostData } from './convertFormDataToPostData';

export const useSubmitForm = ({
  setFormSubmitResult,
  areaKey,
  setError,
}: {
  setFormSubmitResult: Dispatch<SetStateAction<FormSubmitResult | null>>;
  areaKey: string;
  setError: any;
}) => {
  const { getCookies } = useCookies();
  const {
    forms: { useOptimizely },
  } = useConfig();
  const { formatMessage } = useIntl();
  const { language } = useI18n();
  const dispatchGTMEvent = useTracking();
  const formDeviceType = useFormDeviceType();
  const executeUnsubscribe = useExecuteUnsubscribe();
  const { addToast } = useSnackbar();
  const submitForm = async (data: { frequency: string; email: any }) => {
    getCookies()['inspire.optimizely'] === 'true' || useOptimizely
      ? await executeUnsubscribe(data)
          .then(
            (
              response: OperationResult<
                UnsubscribeFromNewsletterMutation | UnsubscribeFlexikontoMutation,
                Exact<{
                  emailAddress: string;
                }>
              >,
            ) => {
              if (response.error) {
                throw new Error(formatMessage(generalFormMessages.errorApi));
              }

              // if no errors
              let resultType: FormSubmitResult['resultType'];
              switch (data.frequency?.split('|')[0]) {
                case 'frequency':
                  resultType = 'FREQUENCY_SUCCESSFUL';
                  break;
                case 'pause':
                  resultType = 'PAUSE_SUCCESSFUL';
                  break;
                default:
                  resultType = 'UNSUBSCRIBE_SUCCESSFUL';
              }
              setFormSubmitResult({ resultType, successful: true });
              dispatchGTMEvent({
                event: 'ga_event',
                eventCategory: 'User Interactions',
                eventLabel: 'newsletter',
                eventAction: 'unsubscribe',
              });
              dispatchGTMEvent<GTMEventGlycerinFormSubmitClick>({
                event: 'FormSubmitClick',
                FormSubmitClickData: {
                  category: 'newsletter-unsubscribe',
                  label: 'registration',
                  detail: 'send',
                  custom: { areaKey },
                },
              });
              if (data.email && data.frequency?.split('|')[0] === 'unsubscribe') {
                dispatchGTMEvent<GTMEventGlycerinUnsubscribe>({
                  event: 'Unsubscribe',
                  UnsubscribeData: {
                    email: data?.email,
                    custom: { areaKey },
                  },
                });
              }
            },
          )
          .catch((error: Error) => {
            addToast({ message: error.toString(), severity: 'error', duration: 2500 });
            dispatchGTMEvent<GTMEventGlycerinDisplayError>({
              event: 'DisplayError',
              DisplayErrorData: {
                category: 'newsletter-unsubscribe',
                label: 'registration',
                detail: 'error',
                errors: [
                  {
                    errorType: 'formValidation',
                    message: error.toString(),
                    severity: GlycerinErrorSeverity.WARNING,
                    source: 'newsletter unsubscribe form',
                  },
                ],
              },
            });
          })
      : await submitMessage<SetErrorType>({
          formatMessage,
          language,
          formDeviceType,
          endpoint: 'change',
          setError,
          convertedFormUnsubNLData: convertFormDataToPostData(areaKey, data),
          onSuccess: (resultType) => {
            setFormSubmitResult({
              resultType: resultType as FormSubmitResult['resultType'],
              successful: true,
            });
            dispatchGTMEvent({
              event: 'ga_event',
              eventCategory: 'User Interactions',
              eventLabel: 'newsletter',
              eventAction: 'unsubscribe',
            });
            dispatchGTMEvent<GTMEventGlycerinFormSubmitClick>({
              event: 'FormSubmitClick',
              FormSubmitClickData: {
                category: 'newsletter-unsubscribe',
                label: 'registration',
                detail: 'send',
                custom: { areaKey },
              },
            });
            if (data.email && data.frequency?.split('|')[0] === 'unsubscribe') {
              dispatchGTMEvent<GTMEventGlycerinUnsubscribe>({
                event: 'Unsubscribe',
                UnsubscribeData: {
                  email: data?.email,
                  custom: { areaKey },
                },
              });
            }
          },
          onError: (errMsg) => {
            addToast({ message: errMsg, severity: 'error', duration: 2500 });
            dispatchGTMEvent<GTMEventGlycerinDisplayError>({
              event: 'DisplayError',
              DisplayErrorData: {
                category: 'newsletter-unsubscribe',
                label: 'registration',
                detail: 'error',
                errors: [
                  {
                    errorType: 'formValidation',
                    message: errMsg,
                    severity: GlycerinErrorSeverity.WARNING,
                    source: 'newsletter unsubscribe form',
                  },
                ],
              },
            });
          },
        });
  };
  return submitForm;
};
